define('core/mixins/withUploadForm',[
    'underscore',
    'backbone',

    'core/strings',
    'core/utils',
    'core/utils/media/upload',
    'core/utils/storage',
    'core/views/media/DragDropUploadView',
    'core/views/media/UploadButtonView',
    'core/views/media/UploadsView',
], function (
    _,
    Backbone,

    strings,
    utils,
    upload,
    storage,
    DragDropUploadView,
    UploadButtonView,
    UploadsView
) {
    'use strict';

    var gettext = strings.get;

    var exports = function withUploadForm() {
        _.defaults(this, exports.bothProto, exports.uploadsProto, exports.previewsProto);
    };


    var ERROR_MESSAGES = {
        'invalid-image-file': gettext(
            'Unfortunately your image upload failed. Please verify ' +
            'that the file is valid and in a supported format (JPEG, PNG, or GIF).'
        ),
        'invalid-content-type': gettext(
            'Unfortunately your image upload failed. Please verify ' +
            'that the file is in a supported format (JPEG, PNG, or GIF).'
        ),
        'file-too-large': gettext(
            'Unfortunately your image upload failed. Please verify ' +
            'that your image is under 5MB.'
        ),
        'not-authenticated': gettext(
            'You must be logged in to upload an image.'
        ),
    };
    var DEFAULT_ERROR_MESSAGE = gettext(
        'Unfortunately your image upload failed. Please verify ' +
        'that your image is in a supported format (JPEG, PNG, or GIF) and ' +
        'under 5MB. If you continue seeing this error, please try again later.'
    );


    // Mixin for methods to preview uploaded media, as well as
    // rich media resulting from a textarea
    exports.previewsProto = {
        initMediaPreviews: function ($el, textarea) {
            // Set up media preview instance
            this.mediaUploadsView = new UploadsView({
                el: $el[0],
            });

            this.mediaUploadsView.render();

            // Update rich media when textarea updates
            this.updateLiveMediaDebounced = _.partial(
                _.debounce(this.updateLiveMedia, 500),
                textarea,
                false  // Not a paste event
            );
            this.listenTo(textarea, {
                keychange: this.updateLiveMediaDebounced,

                paste: function (_evt, options) {
                    if (options && options.fake)
                        return;

                    // We defer the processing since the actual text change
                    // occurs slightly after the paste event
                    _.defer(_.bind(this.updateLiveMedia, this, textarea, true));
                },
            });

            // Also update immediately to display previews for restored drafts
            // (draft restoration is handled in TextareaView.prototype.initialize)
            this.updateLiveMedia(textarea, true);
        },

        clearMediaPreviews: function () {
            if (this.mediaUploadsView)
                this.mediaUploadsView.clear();
        },

        // Called when textarea updates
        // Will update rich media collection based on urls in the textarea
        updateLiveMedia: function (textarea, isPasteEvent) {
            if (!this.mediaUploadsView)
                return;

            var text = textarea.get();
            var cursorPosition = textarea.offset();

            this.mediaUploadsView.richView.updateFromText(
                text,
                cursorPosition,
                { isPasteEvent: isPasteEvent, forum: this.thread.forum.id }
            );
        },
    };

    // Mixin for methods to upload media, via drag-and-drop
    // and an upload button.
    // Depends on: above previewsProto, as well as withAlert.
    exports.uploadsProto = {
        initMediaUploads: function ($dragDropPlaceholder, $dragDropEl, $buttonEl) {
            // Set up drag-and-drop events
            // Note that DragDropUploadView only provides events;
            // it has nothing to render.
            this.mediaDragDropView = new DragDropUploadView({
                el: $dragDropEl[0],
            });
            this.listenTo(this.mediaDragDropView, {
                'uploader:attachMedia': function () {
                    // Avoid notifying user in the future about drag-drop support
                    storage.set('usedDragDrop', 1);

                    // Upload media and update UI
                    this.handleAttachMedia.apply(this, arguments);
                },
                // Expand post area when user drags media over it
                // Cannot bind $el.addClass since $el may change
                'uploader:dragEnter': function () {
                    // TODO: Refactor out of mixin?
                    this.$el.addClass('expanded');
                },
                'uploader:showPlaceholder': function () {
                    $dragDropPlaceholder.show();
                },
                'uploader:hidePlaceholder': function () {
                    $dragDropPlaceholder.hide();
                },
                'uploader:dropError': function () {
                    var errorMessage = gettext(
                        'Sorry we didn\'t catch that. Try again?');
                    this.alert(errorMessage, { type: 'error', isUploadError: true });
                },
            });

            // Set up attach media button events
            this.mediaUploadButtonView = new UploadButtonView({
                el: $buttonEl[0],
            });
            this.listenTo(this.mediaUploadButtonView, {
                'uploader:attachMedia': this.handleUploadViaButton,
            });
            this.mediaUploadButtonView.render();
        },

        handleUploadViaButton: function (files) {
            // Notify user about drag-drop support
            if (files && storage.isPersistent && !storage.get('usedDragDrop') &&
                // Don't show on mobile because ... who actually uses drag and drop on mobile?
                !utils.isMobileUserAgent()) {
                var alertView = this.alert(gettext(
                    'Did you know you can drag and drop images too? Try it now!'));
                this.listenToOnce(alertView, 'dismiss', function () {
                    storage.set('usedDragDrop', 1);
                });
            }

            // Upload media and update UI
            this.handleAttachMedia.apply(this, arguments);
        },

        // Called when the user starts a file upload
        // Will display a progress bar during upload, then update the
        // UI to reflect the upload result
        handleAttachMedia: function (files, options) {
            var self = this;

            var progressModel = new Backbone.Model({
                remainingPerc: 100,
            });
            self.mediaUploadsView.uploadProgress.add(progressModel);

            options = _.extend(options || {}, {
                onProgress: function (percent) {
                    progressModel.set('remainingPerc', 100 - percent);
                },
            });

            var removeProgress = function () {
                self.mediaUploadsView.uploadProgress.remove(progressModel);
            };

            upload.uploadMediaUrl(files, options).then(function (media) {
                // Fetch model to force rich media system to retrieve it immediately.
                // Should help resolve issues where rich media is not available when
                // the post or thread is created.
                media.fetch({ forum: self.thread.forum.id });

                // Update text with url
                self.textarea.insertAtCursor(media.get('url'));

                // Update previews
                self.updateLiveMedia(self.textarea, true);

                self.dismissUploadError();
            }).catch(function (error) {
                var errorMessage;

                if (error && error.code)
                    errorMessage = ERROR_MESSAGES[error.code];

                if (!errorMessage)
                    errorMessage = DEFAULT_ERROR_MESSAGE;

                self.alert(errorMessage, { type: 'error', isUploadError: true });
            }).then(removeProgress, removeProgress);
        },

        dismissUploadError: function () {
            this.dismissAlert(function (alert) {
                return alert.options && alert.options.isUploadError;
            });
        },

        uploadSupported: upload.uploadSupported,

        isUploadInProgress: function () {
            return this.mediaUploadsView &&
                this.mediaUploadsView.uploadProgress.length;
        },
    };

    exports.bothProto = {
        // Initialize media previews and uploads based on options
        // Options:
        //   mediaembedEnabled -- Enables rich media url parsing and previews
        //   allowUploads -- Enables upload UI (drag-drop, button).
        //                   mediaembedEnabled MUST be true if this is.
        //   textarea -- The textarea to look for rich media urls in.
        //               Must be provided if mediaembedEnabled is true.
        //   storageKey -- The key to use for storing uploads as drafts
        // Expects several elements, part of the PostReplyView template, to exist
        initMediaViews: function (options) {
            if (options.mediaembedEnabled || options.gifPickerEnabled) {
                this.initMediaPreviews(
                    this.$('[data-role=media-preview]'),
                    options.textarea
                );
            }

            if (options.allowUploads) {
                this.initMediaUploads(
                    this.$('[data-role=drag-drop-placeholder]'),
                    this.$('[data-role=textarea]'),
                    this.$('[data-role=media-uploader]')
                );
            }
        },
    };

    return exports;
});

